import React, { forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';

export interface AppSelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface AppSelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'children'> {
  options: AppSelectOption[];
  /** `control` matches manual NewControl-style fields: h-10, px-3 / text-sm (aligned with `px-3 py-2` inputs). */
  size?: 'sm' | 'md' | 'control';
  /** Optional element rendered to the left of the value (e.g. an icon); purely cosmetic. */
  leadingIcon?: React.ReactNode;
  /** Tailwind classes for the outer wrapper (sizing, layout). */
  wrapperClassName?: string;
}

const SIZE_CLASSES: Record<NonNullable<AppSelectProps['size']>, string> = {
  sm: 'h-8 text-xs pl-3 pr-9',
  md: 'h-9 text-sm pl-3.5 pr-10',
  control: 'h-10 text-sm pl-3 pr-10 bg-gray-50 border-gray-200 text-gray-900 font-normal',
};

const LEADING_ICON_PADDING: Record<NonNullable<AppSelectProps['size']>, string> = {
  sm: 'pl-8',
  md: 'pl-9',
  control: 'pl-9',
};

/**
 * Reusable styled `<select>`. Uses native semantics (no third-party deps), so it stays
 * accessible and keyboard-friendly while looking consistent across pages: rounded corners,
 * subtle shadow, hover/focus elevation, pointer cursor, and a single chevron indicator.
 */
export const AppSelect = forwardRef<HTMLSelectElement, AppSelectProps>(
  ({ options, size = 'md', leadingIcon, wrapperClassName, className, disabled, ...props }, ref) => {
    const sizeClasses = SIZE_CLASSES[size];
    const leadingPadding = leadingIcon ? LEADING_ICON_PADDING[size] : '';

    return (
      <div className={`relative inline-flex ${wrapperClassName ?? ''}`}>
        {leadingIcon ? (
          <span
            className={`pointer-events-none absolute top-1/2 -translate-y-1/2 ${
              size === 'sm' ? 'left-2.5' : 'left-3'
            } text-gray-400`}
          >
            {leadingIcon}
          </span>
        ) : null}
        <select
          ref={ref}
          disabled={disabled}
          className={[
            'appearance-none w-full font-medium text-gray-700 bg-white',
            'border border-gray-300 rounded-lg shadow-sm',
            'transition-all duration-150',
            'hover:border-gray-400 hover:shadow',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:shadow-md',
            disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
            sizeClasses,
            leadingPadding,
            className ?? '',
          ]
            .filter(Boolean)
            .join(' ')}
          {...props}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          className={`pointer-events-none absolute top-1/2 -translate-y-1/2 ${
            size === 'sm' ? 'right-2 h-3.5 w-3.5' : 'right-2.5 h-4 w-4'
          } ${size === 'control' ? 'text-gray-600' : 'text-gray-500'}`}
          aria-hidden
        />
      </div>
    );
  }
);

AppSelect.displayName = 'AppSelect';
